// ts-rest contract for the Comply dashboard surface.
//
// Phase 3a of /api/db/:table removal (OLD_CODE.html finding #1). The
// `apps/web/src/pages/comply/dashboard/page.tsx` page previously called
// `useTable` against 8 tables (compliance_alerts, breach_register,
// cmp_findings, regulatory_returns, consumer_duty_assessments,
// regulatory_capital_snapshots, suitability_alerts, cmp_review_items)
// and composed five view models in-browser. This contract returns
// those view models already shaped by the server so the page stays a
// thin renderer.
//
// The endpoint is gated to staff who carry the `comply` (or `admin` /
// `view`) persona; non-comply personas get a 403. Reads route through
// the existing typed-store accessors at the service layer.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

const ragStatusSchema = z.enum(['Green', 'Amber', 'Red']);
const pillVariantSchema = z.enum(['blue', 'green', 'amber', 'red', 'grey', 'purple']);

export const complyDashboardKpisSchema = z.object({
    openBreaches: z.number().int(),
    materialOpen: z.number().int(),
    nonMaterialOpen: z.number().int(),
    breachRAG: ragStatusSchema,
    totalOverdue: z.number().int(),
    overdueKYC: z.number().int(),
    overdueCMP: z.number().int(),
    overdueRAG: ragStatusSchema,
    avgConsumerDuty: z.number().int(),
    consumerDutyRAG: ragStatusSchema,
    capitalRatio: z.number(),
    capitalRAG: ragStatusSchema,
});
export type ComplyDashboardKpis = z.infer<typeof complyDashboardKpisSchema>;

export const complyDashboardDeadlineSchema = z.object({
    id: z.string(),
    name: z.string(),
    authority: z.string(),
    period: z.string(),
    dueDate: z.string(),
    status: z.string(),
    preparer: z.string(),
});
export type ComplyDashboardDeadline = z.infer<typeof complyDashboardDeadlineSchema>;

export const complyAlertFeedItemSchema = z.object({
    id: z.string(),
    date: z.string(),
    type: z.string(),
    client: z.string(),
    description: z.string(),
    severity: z.string(),
    status: z.string(),
    section: z.string(),
});
export type ComplyAlertFeedItem = z.infer<typeof complyAlertFeedItemSchema>;

export const complyAttentionItemSchema = z.object({
    id: z.string(),
    ragVariant: pillVariantSchema,
    label: z.string(),
    age: z.string(),
});
export type ComplyAttentionItem = z.infer<typeof complyAttentionItemSchema>;

export const complyDashboardResponseSchema = z.object({
    openCount: z.number().int(),
    kpis: complyDashboardKpisSchema,
    nextDeadline: complyDashboardDeadlineSchema.nullable(),
    alertsFeed: z.array(complyAlertFeedItemSchema),
    attentionItems: z.array(complyAttentionItemSchema),
});
export type ComplyDashboardResponse = z.infer<typeof complyDashboardResponseSchema>;

import { errorBody } from '../schemas/common';

export const complyDashboardContract = c.router(
    {
        get: {
            method: 'GET',
            path: '/comply/dashboard',
            responses: {
                200: complyDashboardResponseSchema,
                403: errorBody,
            },
            summary:
                'Composed Comply-dashboard payload (KPIs, next deadline, alerts feed, attention list)',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
